import React, { useState, useContext, useEffect } from 'react';
import { Button } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { CartContext } from '../../../contexts/CartContext';
import ReduceOptionPopup from './ReduceOptionPopup';

const styles = {
  container: {
    height: 'fit-content',
    width: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  button: {
    minHeight: '0px',
    minWidth: '0px',
    borderRadius: '50%',
    border: '1px solid #C1C1C18F',
    color: '1e1e1e',
    height: '28px',
    width: '28px',
  },
  icon: {
    fontSize: '18px',
    color: '1e1e1e',
  },
  text: {
    fontSize: '14px',
    fontWeight: 600,
  },
  input: {
    fontSize: '14px',
    fontWeight: 600,
    width: '40px',
    textAlign: 'center',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: '#1e1e1e',
    padding: 0,
    margin: 0,
  },
};

function ProductQtyUpdateSec({
  product,
  disableAddButton = false,
  handleOpenOptions,
  compactView = false
}) {

  const {
    addItemToCart,
    reduceItemFromCart,
    setQtyInCart,
    cart
  } = useContext(CartContext);

  const [reducePopupOpen, setReducePopupOpen] = useState(false);
  const [variantsInCart, setVariantsInCart] = useState([]);

  const cartProducts = Object.values(cart?.products || {});

  const productQty = cartProducts.reduce((sum, item) => {
    const sameId = item.product.id === product.id;
    if (!sameId) return sum;

    const itemOption = item.product.selectedOption?.label || null;
    const productOption = product.selectedOption?.label || null;

    if (productOption) {
      return itemOption === productOption ? sum + item.quantity : sum;
    }

    return sum + item.quantity;
  }, 0);

  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (!isTyping) {
      setInputValue(productQty);
    }
  }, [productQty, isTyping]);

  const handleAddClick = (e) => {
    e.stopPropagation();

    const hasOptions = product.options?.product_options?.length > 0;

    if (hasOptions && compactView !== true) {
      handleOpenOptions?.(product);
      return;
    }
    addItemToCart(product);
  };

  const handleReduceClick = (e) => {
    e.stopPropagation();

    if (compactView) {
      reduceItemFromCart(product);
      return;
    }

    const productVariantsInCart = cartProducts.filter(
      (item) => item.product.id === product.id
    );

    if (!product.options?.product_options?.length || productVariantsInCart.length <= 1) {
      reduceItemFromCart(product);
      return;
    }

    setVariantsInCart(productVariantsInCart);
    setReducePopupOpen(true);
  };

  const handleReduceVariant = (variantProduct) => {
    reduceItemFromCart(variantProduct);

    const remaining = variantsInCart
      .map((v) => {
        if (
          v.product.id === variantProduct.id &&
          v.product.selectedOption?.label === variantProduct.selectedOption?.label
        ) {
          const newQty = v.quantity - 1;
          if (newQty <= 0) return null;
          return { ...v, quantity: newQty };
        }
        return v;
      })
      .filter(Boolean);

    setVariantsInCart(remaining);
    if (remaining.length === 0) setReducePopupOpen(false);
  };

  if (productQty === 0) {
    return (
      <Button
        variant="contained"
        sx={{
          backgroundColor: '#E50218',
          color: 'white',
          borderRadius: '20px',
          textTransform: 'none',
          fontSize: '13px',
          fontWeight: 600,
          padding: '4px 16px',
          minHeight: '32px',
          height: '32px',
          '&:hover': {
            backgroundColor: '#C40215',
          },
          boxShadow: 'none',
        }}
        onClick={handleAddClick}
        disabled={disableAddButton}
      >
        Add to Cart
      </Button>
    );
  }

  return (
    <div style={styles.container}>
      <Button
        style={styles.button}
        onClick={handleReduceClick}
        disabled={productQty === 0}
      >
        <RemoveRoundedIcon style={styles.icon} />
      </Button>
      {product?.unit?.toLowerCase() === 'kg' ? (
        <input
          type="text"
          inputMode="decimal"
          style={styles.input}
          value={inputValue}
          onFocus={() => setIsTyping(true)}
          onBlur={() => {
            setIsTyping(false);
            let newQty = parseFloat(inputValue);
            if (isNaN(newQty) || newQty < 0) {
              setInputValue(productQty);
              return;
            }
            if (newQty !== productQty) {
              setQtyInCart(product, newQty);
            }
          }}
          onChange={(e) => setInputValue(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span style={styles.text}>{productQty}</span>
      )}
      <Button
        style={styles.button}
        onClick={handleAddClick}
        disabled={disableAddButton}
      >
        <AddRoundedIcon style={styles.icon} />
      </Button>

      <ReduceOptionPopup
        open={reducePopupOpen}
        onClose={() => setReducePopupOpen(false)}
        productVariants={variantsInCart}
        onReduce={handleReduceVariant}
      />
    </div>
  );
}

export default ProductQtyUpdateSec;
